/**
 * d3 multi-series time-series chart — drop-in for the Highcharts path in
 * `../lineChart.ts`.
 *
 * Axis bounds, tick positions and their labels all arrive precomputed from the
 * Phase 1 geometry helpers, so this file only draws.
 */

import { scaleLinear } from "d3-scale";
import { SERIES_COLORS, SERIES_FADED_COLORS, type LineChartPayload } from "../../normalize.js";
import { lineChartPlotWidth, measureTextWidth } from "../geometry/plotWidth.js";
import { datetimeTicks, datetimeTickLabel } from "../geometry/axisTicks.js";
import { AXIS_COLOR, LABEL_FONT_SIZE, el, labelStyle, makeSvg, makeTooltip , renderResponsive, runAnimation } from "./dom.js";

const LINE_WIDTH = 2;
/**
 * Gap between the value labels and the left edge of the plot. Raised from 8 to 16
 * after the column chart review found 8 cramped — and these labels are much wider
 * ($300,000 against 35), so they need at least as much air.
 */
const Y_LABEL_GAP = 16;
const X_LABEL_GAP = 14;
const END_LABEL_MIN_GAP = 18;
const END_LABEL_OFFSET = 8;
const CROSSHAIR_COLOR = "#ccc";
const HOVER_MARKER_RADIUS = 5;
const HOVER_MARKER_COLOR = "#000";
const TOOLTIP_OFFSET_PX = 8;

const WEEKDAYS = [
  "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday",
] as const;
const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
] as const;

/** Highcharts rendered tooltip dates as `%A, %b %e, %Y`, in UTC. */
function formatTooltipDate(epochMs: number): string {
  const d = new Date(epochMs);
  return `${WEEKDAYS[d.getUTCDay()]}, ${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
}

/** Series are named "NTEN - Pure Gross (%)"; the tooltip drops the trailing unit. */
function seriesTooltipName(name: string): string {
  return name.replace(/\s*\(%\)\s*$/i, "").trim();
}
/** Tick marks hang below the time axis, connecting the rule to each date label. */
const TICK_LENGTH = 10;
/**
 * The d3 chart read slightly taller than the Highcharts original; trimmed from the
 * overall height so the surrounding block matches.
 */
const HEIGHT_TRIM = 10;

export interface D3LineOptions {
  /** Value axis, which does not depend on width. */
  yAxis: { min: number; max: number; ticks: number[]; compactTicks: number[]; formatLabel: (v: number) => string };
  /** Tabbed-pane layout, which uses tighter spacing. */
  embedded: boolean;
  esc: (s: string) => string;
}

function drawLine(
  mount: HTMLElement,
  data: LineChartPayload,
  opts: D3LineOptions,
  animateIn: boolean
): () => void {
  const { yAxis, embedded, esc } = opts;
  const chartWidth = mount.clientWidth;

  // Everything width-dependent is derived here, inside the draw, so a resize
  // recomputes it. Passing these in as fixed options meant a resized chart kept its
  // load-time breakpoint, ticks and height — visible only after a reload.
  const { compact, plotWidth: xPlotWidth } = lineChartPlotWidth({
    chartWidth,
    yAxisLabels: yAxis.ticks.map((v) => yAxis.formatLabel(v)),
    embedded,
  });
  const fontSizePx = compact ? 10 : 11;
  const spacingLeft = compact ? 12 : embedded ? 0 : 20;
  const spacingRight = compact ? 16 : embedded ? 48 : 120;
  const height = (compact ? 300 : 360) - HEIGHT_TRIM;
  const showEndLabels = !compact;
  const labelStep = 1;

  const allX = data.series.flatMap((s) => s.data.map((d) => d[0])).filter((n) => Number.isFinite(n));
  const ticks = datetimeTicks({
    min: allX.length ? Math.min(...allX) : 0,
    max: allX.length ? Math.max(...allX) : 0,
    plotWidth: xPlotWidth,
  });
  const xAxis = {
    min: ticks.axisMin,
    max: ticks.axisMax,
    ticks: ticks.positions,
    formatLabel: (t: number) => datetimeTickLabel(t, ticks.unit),
  };
  const yTicks = compact ? yAxis.compactTicks : yAxis.ticks;

  const widestY = yAxis.ticks.reduce(
    (max, v) =>
      Math.max(
        max,
        measureTextWidth(yAxis.formatLabel(v), {
          fontStack: "var(--an26-font-mono), monospace",
          fontSizePx,
          fontWeight: "300",
        })
      ),
    0
  );

  const plotLeft = spacingLeft + widestY + Y_LABEL_GAP;
  const plotWidth = Math.max(0, chartWidth - plotLeft - spacingRight);
  const plotTop = 10;
  const plotBottom = height - fontSizePx - X_LABEL_GAP - 8;
  const plotHeight = Math.max(0, plotBottom - plotTop);

  const x = scaleLinear().domain([xAxis.min, xAxis.max]).range([plotLeft, plotLeft + plotWidth]);
  const y = scaleLinear().domain([yAxis.min, yAxis.max]).range([plotBottom, plotTop]);

  const svg = makeSvg(chartWidth, height);

  /* value axis labels */
  for (const tick of yTicks) {
    const label = el("text", { x: plotLeft - Y_LABEL_GAP, y: y(tick) + 4, "text-anchor": "end" });
    labelStyle(label, fontSizePx);
    label.textContent = yAxis.formatLabel(tick);
    svg.append(label);
  }

  /* time axis rule + labels */
  svg.append(
    el("line", {
      x1: plotLeft,
      y1: plotBottom,
      x2: plotLeft + plotWidth,
      y2: plotBottom,
      stroke: AXIS_COLOR,
      "stroke-width": 1,
    })
  );
  xAxis.ticks.forEach((tick, ti) => {
    // Every tick gets a mark, even when its label is skipped — on mobile that gives
    // three ticks with two labels, matching Highcharts.
    svg.append(
      el("line", {
        x1: x(tick),
        y1: plotBottom,
        x2: x(tick),
        y2: plotBottom + TICK_LENGTH,
        stroke: AXIS_COLOR,
        "stroke-width": 1,
      })
    );
    if (ti % labelStep !== 0) return;
    const label = el("text", {
      x: x(tick),
      y: plotBottom + TICK_LENGTH + fontSizePx + X_LABEL_GAP - 6,
      "text-anchor": "middle",
    });
    labelStyle(label, fontSizePx);
    // Every date renders in full, centred on its tick — including the last.
    //
    // Highcharts truncates the final label to "Jul…" at some widths, but that is a
    // Highcharts limitation rather than a design intent: spacingRight is 120px
    // (48px embedded), so a ~48px label centred at the plot edge extends only ~24px
    // and nothing is at risk of overflowing. Ellipsis handling was written for this and
    // then removed, because no viewport could actually trigger it.
    label.textContent = xAxis.formatLabel(tick);
    svg.append(label);
  });

  /* series */
  const paths: SVGPathElement[] = [];
  data.series.forEach((series, si) => {
    const color = String(SERIES_COLORS[si % SERIES_COLORS.length]);
    const d = series.data
      .filter(([, v]) => Number.isFinite(v))
      .map(([t, v], i) => `${i === 0 ? "M" : "L"} ${x(t).toFixed(2)} ${y(v).toFixed(2)}`)
      .join(" ");
    const path = el("path", {
      d,
      fill: "none",
      stroke: color,
      "stroke-width": LINE_WIDTH,
      "stroke-linejoin": "round",
    });
    paths.push(path);
    svg.append(path);
  });

  /* end labels, pushed apart when they would collide */
  const ends = !showEndLabels
    ? []
    : data.series
    .map((series, si) => {
      const pts = series.data.filter(([, v]) => Number.isFinite(v));
      const last = pts[pts.length - 1];
      if (!last) return null;
      return {
        si,
        text: series.endLabel ?? yAxis.formatLabel(last[1]),
        x: x(last[0]) + END_LABEL_OFFSET,
        y: y(last[1]) + 4,
      };
    })
        .filter((e): e is NonNullable<typeof e> => e !== null)
        .sort((a, b) => a.y - b.y);

  for (let i = 1; i < ends.length; i += 1) {
    ends[i]!.y = Math.max(ends[i]!.y, ends[i - 1]!.y + END_LABEL_MIN_GAP);
  }
  for (const end of ends) {
    const label = el("text", { x: end.x, y: end.y, "text-anchor": "start" });
    labelStyle(label, fontSizePx);
    label.textContent = end.text;
    svg.append(label);
  }

  mount.replaceChildren(svg);

  /* ---- scroll to the latest data ----
   * The plot keeps a minimum width and its wrapper scrolls, so on narrow viewports the
   * chart opens showing the oldest dates — the least useful end of a performance series.
   * Highcharts ran this on every render, so it re-applies on each redraw rather than
   * only the first: after a rotation the newest data should still be in view.
   *
   * Deferred a frame because scrollWidth is only meaningful once the new SVG has been
   * laid out.
   */
  const scrollWrap = mount.closest<HTMLElement>(".an26-line-chart__scroll");
  let scrollFrame = 0;
  const scrollToLatest = (): void => {
    if (!scrollWrap || scrollWrap.scrollWidth <= scrollWrap.clientWidth) return;
    scrollWrap.scrollLeft = scrollWrap.scrollWidth - scrollWrap.clientWidth;
  };
  scrollFrame = requestAnimationFrame(scrollToLatest);

  // Lines draw themselves in left to right. Highcharts achieves this by animating a
  // clip rect; a dash offset is the equivalent for a plain path and needs no extra
  // elements. getTotalLength() only works once the path is in the document, which is
  // why this runs after replaceChildren.
  const stopAnimation = animateIn
    ? runAnimation((t) => {
        for (const path of paths) {
          const len = path.getTotalLength();
          path.setAttribute("stroke-dasharray", String(len));
          path.setAttribute("stroke-dashoffset", String(len * (1 - t)));
        }
        if (t >= 1) {
          // Drop the dash attributes so they cannot interfere with later redraws.
          for (const path of paths) {
            path.removeAttribute("stroke-dasharray");
            path.removeAttribute("stroke-dashoffset");
          }
        }
      })
    : null;

  const tip = makeTooltip(mount);

  const setFade = (active: number | null): void => {
    paths.forEach((path, si) => {
      const base = String(SERIES_COLORS[si % SERIES_COLORS.length]);
      const faded = String(SERIES_FADED_COLORS[si % SERIES_FADED_COLORS.length]);
      path.setAttribute("stroke", active !== null && si !== active ? faded : base);
    });
  };

  /* ---- hover: crosshair, point marker, tooltip ----
   * Mirrors the Highcharts behaviour: a 1px crosshair at the hovered date, a filled
   * marker on the nearest series' point, the other series faded, and a tooltip
   * carrying the date as well as the value.
   */
  const crosshair = el("line", {
    x1: 0, y1: plotTop, x2: 0, y2: plotBottom,
    stroke: CROSSHAIR_COLOR, "stroke-width": 1,
  });
  crosshair.style.display = "none";
  svg.append(crosshair);

  const marker = el("circle", {
    r: HOVER_MARKER_RADIUS,
    fill: HOVER_MARKER_COLOR,
    stroke: HOVER_MARKER_COLOR,
    "stroke-width": 2,
  });
  marker.style.display = "none";
  svg.append(marker);

  /** Points, pre-filtered once so hover does not re-scan the raw series each move. */
  const seriesPoints = data.series.map((sr) => sr.data.filter(([, v]) => Number.isFinite(v)));

  const onMove = (ev: MouseEvent): void => {
    const box = svg.getBoundingClientRect();
    const px = ev.clientX - box.x;
    const py = ev.clientY - box.y;
    if (px < plotLeft || px > plotLeft + plotWidth) return;

    const t = xAxis.min + ((px - plotLeft) / plotWidth) * (xAxis.max - xAxis.min);

    // Nearest point in time, then the series whose value at that time is closest to
    // the pointer — so hovering near a line selects that line, not always the first.
    let best: { si: number; point: [number, number]; dy: number } | null = null;
    seriesPoints.forEach((points, si) => {
      let nearest = points[0];
      for (const point of points) {
        if (Math.abs(point[0] - t) < Math.abs((nearest?.[0] ?? 0) - t)) nearest = point;
      }
      if (!nearest) return;
      const dy = Math.abs(y(nearest[1]) - py);
      if (!best || dy < best.dy) best = { si, point: nearest, dy };
    });
    if (!best) return;

    const { si, point } = best;
    const cx = x(point[0]);
    const cy = y(point[1]);

    crosshair.setAttribute("x1", String(cx));
    crosshair.setAttribute("x2", String(cx));
    crosshair.style.display = "";

    marker.setAttribute("cx", String(cx));
    marker.setAttribute("cy", String(cy));
    marker.style.display = "";

    setFade(si);

    const name = seriesTooltipName(data.series[si]?.name ?? "");
    tip.innerHTML =
      `<div class="an26-tooltip-hc">` +
      `<div class="an26-tooltip-hc__date">${esc(formatTooltipDate(point[0]))}</div>` +
      `<div class="an26-tooltip-hc__line">${esc(name)}: ` +
      `<span class="an26-tooltip-hc__val">${esc(yAxis.formatLabel(point[1]))}</span></div></div>`;
    tip.style.display = "block";
    tip.style.left = `${cx + TOOLTIP_OFFSET_PX}px`;
    tip.style.top = `${cy - TOOLTIP_OFFSET_PX * 2}px`;
  };

  const onLeave = (): void => {
    setFade(null);
    crosshair.style.display = "none";
    marker.style.display = "none";
    tip.style.display = "none";
  };

  svg.addEventListener("mousemove", onMove);
  mount.addEventListener("mouseleave", onLeave);

  return () => {
    cancelAnimationFrame(scrollFrame);
    stopAnimation?.();
    svg.removeEventListener("mousemove", onMove);
    mount.removeEventListener("mouseleave", onLeave);
    mount.replaceChildren();
  };
}

/**
 * Public entry point: draws immediately and redraws on container resize, so the
 * chart stays correct when a device rotates or a layout reflows.
 */
export function renderD3LineChart(
  mount: HTMLElement,
  data: LineChartPayload,
  opts: D3LineOptions
): () => void {
  return renderResponsive(mount, (animateIn) => drawLine(mount, data, opts, animateIn));
}
